import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, PriceBreakdown } from './CheckoutSummaryElements';

export const ClassicTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center"
      style={{
        backgroundColor: config.backgroundColor || '#f1f5f9',
        color: config.textColor || '#1e293b',
        fontFamily: config.fontFamily || 'Inter, sans-serif',
      }}
    >
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        {/* Top Header Bar */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-5 brightness-0 invert object-contain" />}
            <span className="text-xs font-bold tracking-wider text-slate-300">Enterprise Order Desk</span>
          </div>
          <span className="text-[11px] font-semibold text-emerald-400 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400"></span> 256-Bit SSL Encrypted
          </span>
        </div>

        {/* Content Body Split */}
        <div className="grid grid-cols-1 md:grid-cols-12 divide-y md:divide-y-0 md:divide-x divide-slate-200">
          {/* Order Summary */}
          <div className="md:col-span-5 p-6 space-y-5 bg-slate-50/60">
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Plan Selected</p>
              <h3 className="text-xl font-bold text-slate-900 mt-0.5">{plan.tier}</h3>
              {plan.description && <p className="text-xs text-slate-500 mt-1">{plan.description}</p>}
            </div>

            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />

            <div className="pt-2">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Service Entitlements</p>
              <PlanFeatureList features={plan.features} />
            </div>

            <PriceBreakdown plan={plan} billingCycle={billingCycle} addonsTotal={addonsTotal} discountAmount={discountAmount} finalTotal={finalTotal} currencySymbol={currencySymbol} />
          </div>

          {/* Checkout Info */}
          <div className="md:col-span-7 p-6 space-y-5">
            <div>
              <h2 className="text-lg font-bold text-slate-900">{config.headerMain || 'Billing Information'}</h2>
              <p className="text-xs text-slate-500">{config.headerSub || 'Please provide your account details.'}</p>
            </div>

            <SocialProofAndFomo config={config} />

            <form onSubmit={onSubmitInfo} className="space-y-4">
              <FormFields config={config} formData={formData} onInputChange={onInputChange} />
              <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
              <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

              <button
                type="submit"
                disabled={isCapturing}
                className="w-full py-3 text-xs font-bold uppercase tracking-wider text-white shadow transition-all hover:opacity-90 disabled:opacity-50"
                style={{
                  backgroundColor: config.primaryColor || '#0f172a',
                  borderRadius: config.buttonRadius || '6px',
                }}
              >
                {isCapturing ? 'Submitting Details...' : config.buttonText || 'Authorize & Pay'}
              </button>
            </form>

            <TrustAndSecurityBadge config={config} />
            {config.footerText && <p className="text-[10px] text-slate-400 text-center">{config.footerText}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
